import * as React from 'react';

import { useFormContext } from 'react-hook-form';

import {
  Box,
  Collapse,
  Divider,
  Grid,
  InputAdornment,
} from '@mui/material';

import { FlightEventReport, RadioGroupOption } from '../../../core/api/clients';

import ControlledCascadingSelect from '../../../components/ControlledCascadingSelect';

import ControlledTextField from '../../../components/ControlledTextField';
import ControlledTimeField from '../../../components/ControlledTimeField';
import ControlledDatePicker from '../../../components/ControlledDatePicker';
import ControlledRadioGroup from '../../../components/ControlledRadioGroup';
import ControlledSelect from '../../../components/ControlledSelect';

import { useFlightEventFieldOptions } from '../../../core/context/FlightEventFieldOptionsContext';
import { transformToUpperCase } from '../../../core/textTransforms';
import { useFlightEventReportData } from '../../../core/context/FlightEventReportDataContext';

function EventStep({ visible, readOnly }: { visible: boolean, readOnly?: boolean }) {
  const { eventOptions, phaseOptions } = useFlightEventFieldOptions();
  const { control, setValue, resetField } = useFormContext<FlightEventReport>();
  const { allowedActions } = useFlightEventReportData();

  return (
    <Collapse in={visible}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Box display="flex">
            <ControlledTextField<FlightEventReport>
              name="eventTitle"
              control={control}
              rules={{ required: true }}
              readOnly={readOnly}
              disableEditPopper
            />
          </Box>
        </Grid>
        <Grid item xs={12} md={4}>
          <ControlledTextField<FlightEventReport>
            name="locationOfEvent"
            label="Location of Event"
            control={control}
            readOnly={readOnly}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <ControlledDatePicker<FlightEventReport>
            name="eventDate"
            control={control}
            readOnly={readOnly}
            rules={{ required: true }}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <ControlledTimeField
            name="localEventTime"
            control={control}
            readOnly={readOnly}
            rules={{ required: true }}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
        <Grid item xs={12}>
          <ControlledCascadingSelect<FlightEventReport>
            groupName="eventType"
            name="eventType"
            control={control}
            fieldNames={['eventType', 'eventType2', 'eventType3']}
            options={eventOptions}
            rules={{ required: true }}
            readOnly={readOnly}
            setValue={setValue}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
      </Grid>
      <Divider sx={{ my: 2 }} />
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <ControlledSelect<FlightEventReport>
            name="typeOfFlight"
            label="Type of Flight"
            control={control}
            menuItems={[
              { name: 'Revenue (part 121)', value: 'Revenue (part 121)' },
              { name: 'Non-Revenue (part 91)', value: 'Non-Revenue (part 91)' },
              { name: 'Maintenance Test', value: 'Maintenance Test' },
              { name: 'Special Flight Permit', value: 'Special Flight Permit' },
            ]}
            readOnly={readOnly}
            rules={{ required: true }}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <ControlledSelect<FlightEventReport>
            name="operationalImpact"
            control={control}
            menuItems={[
              { name: 'Air Turnback', value: 'Air Turnback' },
              { name: 'Diversion/Unscheduled Landing', value: 'Diversion/Unscheduled Landing' },
              { name: 'Gate Return', value: 'Gate Return' },
              { name: 'Rejected Takeoff', value: 'Rejected Takeoff' },
              { name: 'No Disruption', value: 'No Disruption' },
              { name: 'Flight Delay', value: 'Flight Delay' },
            ]}
            readOnly={readOnly}
            rules={{ required: true }}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <ControlledTextField<FlightEventReport>
            name="numberOfPassengers"
            label="Number of Passengers"
            control={control}
            readOnly={readOnly}
            disableEditPopper={!allowedActions?.edit}
            TextFieldProps={{
              inputProps: {
                onBlur: (event) => {
                  const { value } = event.target;
                  const nan = Number.isNaN(Number(value));
                  if (value === '') {
                    setValue('numberOfPassengers', null);
                  } else if (nan) {
                    resetField('numberOfPassengers', {
                      keepDirty: false,
                      keepError: false,
                      keepTouched: false,
                    });
                  }
                },
              },
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <ControlledSelect<FlightEventReport>
            name="flightDeckObserver"
            control={control}
            menuItems={[
              { name: 'N/A', value: 'N/A' },
              { name: 'Government Agency', value: 'Government Agency' },
              { name: 'Offline', value: 'Offline' },
              { name: 'Air Wisconsin', value: 'Air Wisconsin' },
            ]}
            readOnly={readOnly}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <ControlledTextField<FlightEventReport>
            name="flightDeckObserverInformation"
            control={control}
            readOnly={readOnly}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <ControlledRadioGroup<FlightEventReport>
            RadioGroupProps={{ row: true }}
            name="pilotFlying"
            control={control}
            radioOptions={[
              { value: 'captain', label: 'Captain' },
              { value: 'firstOfficer', label: 'First Officer' },
            ]}
            readOnly={readOnly}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <ControlledTextField<FlightEventReport>
            name="altitude"
            control={control}
            readOnly={readOnly}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <ControlledSelect<FlightEventReport>
            name="surfaceCondition"
            control={control}
            menuItems={[
              { value: 'Dry', name: 'Dry' },
              { value: 'Wet', name: 'Wet' },
              { value: 'Standing Water', name: 'Standing Water' },
              { value: 'Slush', name: 'Slush' },
              { value: 'Wet Snow', name: 'Wet Snow' },
              { value: 'Dry Snow', name: 'Dry Snow' },
              { value: 'Compact Snow', name: 'Compact Snow' },
              { value: 'Ice', name: 'Ice' },
            ]}
            readOnly={readOnly}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <ControlledTextField<FlightEventReport>
            name="maxGroundSpeed"
            control={control}
            readOnly={readOnly}
            TextFieldProps={{
              InputProps: {
                endAdornment: <InputAdornment position="end">kts</InputAdornment>,
              },
            }}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <ControlledTextField<FlightEventReport>
            name="runwayDesignation"
            label="Runway"
            control={control}
            readOnly={readOnly}
            transform={transformToUpperCase}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <ControlledTextField<FlightEventReport>
            name="airspeed"
            control={control}
            readOnly={readOnly}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <ControlledRadioGroup<FlightEventReport>
            RadioGroupProps={{ row: true }}
            name="airspeedType"
            control={control}
            radioOptions={[
              { value: 'Indicated', label: 'Indicated' },
              { value: 'Mach', label: 'Mach' },
            ]}
            readOnly={readOnly}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
        <Grid item xs={12}>
          <ControlledCascadingSelect<FlightEventReport>
            groupName="phaseOfFlight"
            name="phaseOfFlight"
            control={control}
            fieldNames={['phaseOfFlight', 'phaseOfFlight2']}
            options={phaseOptions}
            rules={{ required: true }}
            readOnly={readOnly}
            setValue={setValue}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
      </Grid>
      <Divider sx={{ my: 2 }} />
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
          <ControlledRadioGroup<FlightEventReport>
            RadioGroupProps={{ row: true }}
            control={control}
            label="Emergency Declared"
            name="emergencyDeclared"
            radioOptions={[
              { label: 'Yes', value: RadioGroupOption.Yes },
              { label: 'No', value: RadioGroupOption.No },
            ]}
            readOnly={readOnly}
            rules={{ required: true }}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
          <ControlledRadioGroup<FlightEventReport>
            RadioGroupProps={{ row: true }}
            control={control}
            label="Cabin Braced"
            name="cabinBraced"
            radioOptions={[
              { label: 'Yes', value: RadioGroupOption.Yes },
              { label: 'No', value: RadioGroupOption.No },
            ]}
            readOnly={readOnly}
            rules={{ required: true }}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
          <ControlledRadioGroup<FlightEventReport>
            RadioGroupProps={{ row: true }}
            control={control}
            label="Seat Belt Sign"
            name="seatBeltSign"
            radioOptions={[
              { label: 'On', value: RadioGroupOption.Yes },
              { label: 'Off', value: RadioGroupOption.No },
            ]}
            readOnly={readOnly}
            rules={{ required: true }}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
          <ControlledRadioGroup<FlightEventReport>
            RadioGroupProps={{ row: true }}
            control={control}
            label="Evacuation"
            name="evacuation"
            radioOptions={[
              { label: 'Yes', value: RadioGroupOption.Yes },
              { label: 'No', value: RadioGroupOption.No },
            ]}
            readOnly={readOnly}
            rules={{ required: true }}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
          <ControlledRadioGroup<FlightEventReport>
            RadioGroupProps={{ row: true }}
            control={control}
            label="Overweight Landing"
            name="overweightLanding"
            radioOptions={[
              { label: 'Yes', value: RadioGroupOption.Yes },
              { label: 'No', value: RadioGroupOption.No },
            ]}
            readOnly={readOnly}
            rules={{ required: true }}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
          <ControlledRadioGroup<FlightEventReport>
            RadioGroupProps={{ row: true }}
            control={control}
            label="ARFF Requested"
            name="arffRequested"
            radioOptions={[
              { label: 'Yes', value: RadioGroupOption.Yes },
              { label: 'No', value: RadioGroupOption.No },
            ]}
            readOnly={readOnly}
            rules={{ required: true }}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
          <ControlledRadioGroup<FlightEventReport>
            RadioGroupProps={{ row: true }}
            control={control}
            label="ARFF Responded"
            name="arffResponded"
            radioOptions={[
              { label: 'Yes', value: RadioGroupOption.Yes },
              { label: 'No', value: RadioGroupOption.No },
            ]}
            readOnly={readOnly}
            rules={{ required: true }}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
          <ControlledRadioGroup<FlightEventReport>
            RadioGroupProps={{ row: true }}
            control={control}
            label="EMS Requested"
            name="emsRequested"
            radioOptions={[
              { label: 'Yes', value: RadioGroupOption.Yes },
              { label: 'No', value: RadioGroupOption.No },
            ]}
            readOnly={readOnly}
            rules={{ required: true }}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
          <ControlledRadioGroup<FlightEventReport>
            RadioGroupProps={{ row: true }}
            control={control}
            label="EMS Responded"
            name="emsResponded"
            radioOptions={[
              { label: 'Yes', value: RadioGroupOption.Yes },
              { label: 'No', value: RadioGroupOption.No },
            ]}
            readOnly={readOnly}
            rules={{ required: true }}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
          <ControlledRadioGroup<FlightEventReport>
            RadioGroupProps={{ row: true }}
            control={control}
            label="Security Requested"
            name="securityRequested"
            radioOptions={[
              { label: 'Yes', value: RadioGroupOption.Yes },
              { label: 'No', value: RadioGroupOption.No },
            ]}
            readOnly={readOnly}
            rules={{ required: true }}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
          <ControlledRadioGroup<FlightEventReport>
            RadioGroupProps={{ row: true }}
            control={control}
            label="Security Responded"
            name="securityResponded"
            radioOptions={[
              { label: 'Yes', value: RadioGroupOption.Yes },
              { label: 'No', value: RadioGroupOption.No },
            ]}
            readOnly={readOnly}
            rules={{ required: true }}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
          <ControlledRadioGroup<FlightEventReport>
            RadioGroupProps={{ row: true }}
            control={control}
            label="Emergency Med Kit Used"
            name="emergencyMedKitUsed"
            radioOptions={[
              { label: 'Yes', value: RadioGroupOption.Yes },
              { label: 'No', value: RadioGroupOption.No },
            ]}
            readOnly={readOnly}
            rules={{ required: true }}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
          <ControlledRadioGroup<FlightEventReport>
            RadioGroupProps={{ row: true }}
            control={control}
            label="AED Used"
            name="aedUsed"
            radioOptions={[
              { label: 'Yes', value: RadioGroupOption.Yes },
              { label: 'No', value: RadioGroupOption.No },
            ]}
            readOnly={readOnly}
            rules={{ required: true }}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
          <ControlledRadioGroup<FlightEventReport>
            RadioGroupProps={{ row: true }}
            control={control}
            label="Portable Oxygen Used"
            name="portableOxygenUsed"
            radioOptions={[
              { label: 'Yes', value: RadioGroupOption.Yes },
              { label: 'No', value: RadioGroupOption.No },
            ]}
            readOnly={readOnly}
            rules={{ required: true }}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
          <ControlledRadioGroup<FlightEventReport>
            RadioGroupProps={{ row: true }}
            control={control}
            label="Aircraft Taxi to Gate"
            name="aircraftTaxiToGate"
            radioOptions={[
              { label: 'Yes', value: RadioGroupOption.Yes },
              { label: 'No', value: RadioGroupOption.No },
            ]}
            readOnly={readOnly}
            rules={{ required: true }}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
          <ControlledRadioGroup<FlightEventReport>
            RadioGroupProps={{ row: true }}
            control={control}
            label="Coordination with Dispatch"
            name="coordinationWithDispatch"
            radioOptions={[
              { label: 'Yes', value: RadioGroupOption.Yes },
              { label: 'No', value: RadioGroupOption.No },
            ]}
            readOnly={readOnly}
            rules={{ required: true }}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
      </Grid>
    </Collapse>
  );
}

export default EventStep;
